import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  FormHelperText,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Error as ErrorIcon,
  NetworkCheck,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import { Category, Customer } from '../../types/exporter';
import { exporterService } from '../../../../../api/cansense/exporterApi';

// ============================================================================
// Types
// ============================================================================

export interface AddExporterFormData {
  displayName: string;
  host: string;
  port: number;
  exporterType: string;
  customerId: number | null;
  categoryId: number | null;
  authType: 'none' | 'basic' | 'bearer';
  authUsername?: string;
  authPassword?: string;
  authToken?: string;
}

interface ConnectionTestResult {
  success: boolean;
  exporterType?: string;
  exporterVersion?: string;
  metricsCount?: number;
  responseTime?: number;
  error?: string;
}

interface AddExporterDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: AddExporterFormData) => Promise<boolean>;
  categories?: Category[];
  customers?: Customer[];
  existingHosts?: string[];
}

// ============================================================================
// Constants
// ============================================================================

const EXPORTER_TYPES = [
  { value: 'node_exporter', label: 'Node Exporter', description: 'Linux/Unix server monitoring' },
  { value: 'windows_exporter', label: 'Windows Exporter', description: 'Windows server monitoring' },
  { value: 'blackbox', label: 'Blackbox Exporter', description: 'Probe-based monitoring (HTTP, TCP, ICMP)' },
  { value: 'snmp', label: 'SNMP Exporter', description: 'Network device monitoring via SNMP' },
  { value: 'mysql', label: 'MySQL Exporter', description: 'MySQL database monitoring' },
  { value: 'postgres', label: 'PostgreSQL Exporter', description: 'PostgreSQL database monitoring' },
  { value: 'redis', label: 'Redis Exporter', description: 'Redis server monitoring' },
  { value: 'custom', label: 'Custom Exporter', description: 'Other Prometheus-compatible exporter' }
];

const DEFAULT_PORTS: Record<string, number> = {
  node_exporter: 9100,
  windows_exporter: 9182,
  blackbox: 9115,
  snmp: 9116,
  mysql: 9104,
  postgres: 9187,
  redis: 9121,
  custom: 9090
};

// ============================================================================
// Component
// ============================================================================

export const AddExporterDialog: React.FC<AddExporterDialogProps> = ({
  open,
  onClose,
  onAdd,
  categories = [],
  customers = [],
  existingHosts = []
}) => {
  const theme = useTheme();

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState<number>(9100);
  const [exporterType, setExporterType] = useState('node_exporter');
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);

  // Auth state
  const [authType, setAuthType] = useState<'none' | 'basic' | 'bearer'>('none');
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [error, setError] = useState('');
  const [addAnywayChecked, setAddAnywayChecked] = useState(false);

  // Reset form when dialog closes
  const handleClose = () => {
    setDisplayName('');
    setHost('');
    setPort(9100);
    setExporterType('node_exporter');
    setCustomerId(null);
    setCategoryId(null);
    setAuthType('none');
    setAuthUsername('');
    setAuthPassword('');
    setAuthToken('');
    setShowPassword(false);
    setTestResult(null);
    setError('');
    setAddAnywayChecked(false);
    onClose();
  };

  // Map exporter types to default category names
  const EXPORTER_TYPE_TO_CATEGORY: Record<string, string> = {
    node_exporter: 'Infrastructure',
    windows_exporter: 'Infrastructure',
    blackbox: 'Web',
    snmp: 'Network',
    mysql: 'Database',
    postgres: 'Database',
    redis: 'Database',
    custom: 'Infrastructure',
  };

  // Update port and auto-select category when exporter type changes
  useEffect(() => {
    if (exporterType && DEFAULT_PORTS[exporterType]) {
      setPort(DEFAULT_PORTS[exporterType]);
    }
    // Auto-select category based on exporter type
    const categoryName = EXPORTER_TYPE_TO_CATEGORY[exporterType];
    if (categoryName && categories.length > 0) {
      const matched = categories.find((c) => c.name === categoryName);
      if (matched) setCategoryId(matched.id);
    }
  }, [exporterType, categories]);

  // Validate host format
  const validateHost = (value: string): boolean => {
    if (!value) return false;
    // Allow hostname, IP address, or FQDN
    const hostPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/;
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    return hostPattern.test(value) || ipPattern.test(value);
  };

  // Check for duplicate host:port
  const isDuplicate = (host: string, port: number): boolean => {
    const target = `${host}:${port}`;
    return existingHosts.includes(target);
  };

  // Handle host change
  const handleHostChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newHost = event.target.value.trim();
    setHost(newHost);
    setTestResult(null);
    setAddAnywayChecked(false);
    setError('');

    if (newHost && !validateHost(newHost)) {
      setError('Invalid host format. Enter a hostname or IP address.');
    } else if (newHost && isDuplicate(newHost, port)) {
      setError('This host:port combination is already being monitored');
    }
  };

  // Handle port change
  const handlePortChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPort = parseInt(event.target.value, 10);
    if (!isNaN(newPort) && newPort > 0 && newPort <= 65535) {
      setPort(newPort);
      setTestResult(null);
      setAddAnywayChecked(false);
      if (host && isDuplicate(host, newPort)) {
        setError('This host:port combination is already being monitored');
      } else {
        setError('');
      }
    }
  };

  // Handle exporter type change
  const handleExporterTypeChange = (event: SelectChangeEvent) => {
    const newType = event.target.value;
    setExporterType(newType);
    setTestResult(null);
    setAddAnywayChecked(false);
  };

  // Test connection
  const handleTestConnection = async () => {
    if (!host || !port) {
      setError('Please enter host and port');
      return;
    }

    if (!validateHost(host)) {
      setError('Invalid host format');
      return;
    }

    setIsTesting(true);
    setError('');
    setTestResult(null);

    try {
      const result = await exporterService.testConnection({
        host,
        port,
        authType,
        authUsername: authType === 'basic' ? authUsername : undefined,
        authPassword: authType === 'basic' ? authPassword : undefined,
        authToken: authType === 'bearer' ? authToken : undefined
      });

      setTestResult(result);

      if (result.success && result.exporterType) {
        // Auto-detect exporter type if possible
        const detectedType = EXPORTER_TYPES.find(t =>
          t.value === result.exporterType ||
          result.exporterType?.toLowerCase().includes(t.value.replace('_', ''))
        );
        if (detectedType) {
          setExporterType(detectedType.value);
        }
      }

      if (!result.success) {
        setError(result.error || 'Connection failed');
      }
    } catch (err) {
      setError('Failed to test connection');
      console.error('Connection test failed:', err);
    } finally {
      setIsTesting(false);
    }
  };

  // Handle form submission
  const handleAdd = async () => {
    if (!host || !port) {
      setError('Please enter host and port');
      return;
    }

    if (!testResult?.success && !addAnywayChecked) {
      setError('Please test the connection first, or check "Add anyway"');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData: AddExporterFormData = {
        displayName: displayName || `${host}:${port}`,
        host,
        port,
        exporterType,
        customerId,
        categoryId,
        authType,
        authUsername: authType === 'basic' ? authUsername : undefined,
        authPassword: authType === 'basic' ? authPassword : undefined,
        authToken: authType === 'bearer' ? authToken : undefined
      };

      const success = await onAdd(formData);
      if (success) {
        handleClose();
      } else {
        setError('Failed to add exporter');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add exporter';
      setError(message);
      console.error('Add exporter failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Check if form is valid
  const isFormValid = host && port && (addAnywayChecked || (!error && testResult?.success));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Add sx={{ mr: 1 }} />
          Add Exporter
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Connection Section */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Connection Details
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Host"
              placeholder="e.g., server1.example.com or 192.168.1.100"
              value={host}
              onChange={handleHostChange}
              error={!!error && !testResult}
              helperText={error && !testResult ? error : 'Hostname or IP address'}
              sx={{ flex: 2 }}
            />
            <TextField
              label="Port"
              type="number"
              value={port}
              onChange={handlePortChange}
              sx={{ flex: 1, minWidth: 100 }}
              inputProps={{ min: 1, max: 65535 }}
            />
            <Button
              variant="outlined"
              onClick={handleTestConnection}
              disabled={!host || !port || isTesting || !!error}
              startIcon={isTesting ? <CircularProgress size={16} /> : <NetworkCheck />}
              sx={{ height: 56, minWidth: 140 }}
            >
              {isTesting ? 'Testing...' : 'Test Connection'}
            </Button>
          </Box>

          {/* Test Results */}
          {testResult && (
            <Box sx={{ mb: 3, p: 2, bgcolor: theme.palette.grey[50], borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {testResult.success ? (
                  <CheckCircle sx={{ color: theme.palette.success.main, mr: 1 }} />
                ) : (
                  <ErrorIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
                )}
                <Typography variant="subtitle2">
                  {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                </Typography>
              </Box>

              {testResult.success && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {testResult.exporterType && (
                    <Chip label={`Type: ${testResult.exporterType}`} size="small" color="primary" />
                  )}
                  {testResult.exporterVersion && (
                    <Chip label={`Version: ${testResult.exporterVersion}`} size="small" />
                  )}
                  {testResult.metricsCount && (
                    <Chip label={`Metrics: ${testResult.metricsCount}`} size="small" />
                  )}
                  {testResult.responseTime && (
                    <Chip
                      label={`Response: ${testResult.responseTime}ms`}
                      size="small"
                      color={testResult.responseTime > 1000 ? 'warning' : 'success'}
                    />
                  )}
                </Box>
              )}

              {testResult.error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {testResult.error}
                </Alert>
              )}

              {!testResult.success && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={addAnywayChecked}
                      onChange={(e) => setAddAnywayChecked(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Add anyway (pending status — will retry connection later)
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Exporter Details Section */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Exporter Details
          </Typography>

          <TextField
            fullWidth
            label="Display Name"
            placeholder="e.g., Production Web Server"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            helperText="Optional friendly name for this exporter"
            sx={{ mb: 3 }}
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Exporter Type</InputLabel>
            <Select
              value={exporterType}
              onChange={handleExporterTypeChange}
              label="Exporter Type"
            >
              {EXPORTER_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {type.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {type.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Customer</InputLabel>
              <Select
                value={customerId ?? ''}
                onChange={(e) => setCustomerId(e.target.value === '' ? null : Number(e.target.value))}
                label="Customer"
              >
                <MenuItem value="">
                  <em>Internal / No Customer</em>
                </MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.cust_id} value={customer.cust_id}>
                    {customer.cust_name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Optional - assign to a customer</FormHelperText>
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryId ?? ''}
                onChange={(e) => setCategoryId(e.target.value === '' ? null : Number(e.target.value))}
                label="Category"
              >
                <MenuItem value="">
                  <em>No Category</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Optional - group by category</FormHelperText>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Authentication Section */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Authentication (Optional)
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Authentication Type</InputLabel>
            <Select
              value={authType}
              onChange={(e) => setAuthType(e.target.value as 'none' | 'basic' | 'bearer')}
              label="Authentication Type"
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="basic">Basic Auth (Username/Password)</MenuItem>
              <MenuItem value="bearer">Bearer Token</MenuItem>
            </Select>
          </FormControl>

          {authType === 'basic' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Username"
                value={authUsername}
                onChange={(e) => setAuthUsername(e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ minWidth: 'auto' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  )
                }}
              />
            </Box>
          )}

          {authType === 'bearer' && (
            <TextField
              fullWidth
              label="Bearer Token"
              type={showPassword ? 'text' : 'password'}
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <Button
                    size="small"
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{ minWidth: 'auto' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </Button>
                )
              }}
            />
          )}

          {/* Info Alert */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Note:</strong> After adding the exporter, Prometheus will begin scraping metrics
              within 30 seconds. View monitoring data in the Grafana dashboard once collection starts.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleAdd}
          variant="contained"
          disabled={!isFormValid}
          loading={submitting}
          startIcon={<Add />}
        >
          Add Exporter
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddExporterDialog;
